import { lazy, Suspense } from "react";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading, Reveal } from "@/components/Reveal";
import { scrollToId } from "@/components/Navbar";

const Bars3D = lazy(() => import("@/components/visuals/Bars3D"));

const COMMERCE_POINTS = [
  {
    title: "Amazon — our specialty",
    text: "FBA launches, dropshipping operations, Private Label and White Label product lines managed end to end.",
  },
  {
    title: "Shopify stores that convert",
    text: "Custom-built storefronts with any feature integrated — subscriptions, bundles, loyalty, anything.",
  },
  {
    title: "eBay & Etsy dropshipping",
    text: "Proven setups, product strategy and store management for both platforms.",
  },
  {
    title: "Found on Google",
    text: "SEO and digital marketing baked in — so your store gets traffic, not just a launch.",
  },
];

export const CommerceSpotlight = () => {
  return (
    <section
      id="commerce"
      className="py-16 sm:py-20 lg:py-24 bg-card/40 border-y border-border"
      data-testid="commerce-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <SectionHeading
            eyebrow="E-Commerce Expertise"
            title="From first listing to seven figures"
            description="Selling online is not a side service for us — it is a core discipline. Whether you want to launch on Amazon, scale a Shopify store or run your own marketplace, you work with someone who has done it."
          />

          <div className="mt-8 space-y-5">
            {COMMERCE_POINTS.map((point, i) => (
              <Reveal key={point.title} delay={i * 0.08} className="flex gap-4">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground">{point.title}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-1">{point.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.3} className="mt-9">
            <Button
              onClick={() => scrollToId("contact")}
              variant="outline"
              className="rounded-xl border-primary/40 text-primary hover:bg-primary/10 hover:text-primary transition-colors duration-200"
              data-testid="commerce-contact-cta-button"
            >
              Start Selling Online <ArrowUpRight className="ml-2 h-4 w-4" />
            </Button>
          </Reveal>
        </div>

        <Reveal className="lg:col-span-6">
          <div className="relative">
            <div className="img-frame w-full h-[340px] sm:h-[440px] lg:h-[520px] bg-[#0d0e14] overflow-hidden flex flex-col" data-testid="commerce-3d-chart">
              <div className="flex items-center justify-between px-6 pt-6">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">
                  Client Revenue Growth · 3D View
                </span>
                <span className="text-xs font-semibold bg-primary/15 text-primary border border-primary/30 rounded-full px-3 py-1">
                  6 Months
                </span>
              </div>
              <div className="flex-1">
                <Suspense fallback={<Skeleton className="w-full h-full bg-secondary/40" />}>
                  <Bars3D />
                </Suspense>
              </div>
              <div className="flex justify-between px-8 pb-5 text-[10px] font-mono-meta text-muted-foreground">
                <span>Launch</span>
                <span>Scale</span>
                <span className="text-primary">+64% Revenue</span>
              </div>
            </div>
            <div className="absolute -bottom-5 -left-3 sm:-left-5 bg-card border border-border rounded-2xl px-5 py-4 shadow-[0_18px_60px_rgba(0,0,0,0.5)]">
              <p className="text-2xl font-semibold text-primary">4 Platforms</p>
              <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">
                Amazon · Shopify · eBay · Etsy
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
